<template>
    <div id="PayPalForm">
        <div class="form_style" :class="{ disabled: !submittable || !agreeTerms || (kerbsideOption && !kerbside) }">

            <!-- PayPal Buttons -->
            <div id="paypal-button-container" class="paypal-button-container"></div>

            <!-- Google Pay Button Container -->
            <div id="google-pay-container" class="google-pay-container"></div>

            <!-- Card Fields Form -->
            <div id="card-form" class="card-fields-container">
                <div class="paypal_divider">
                    <span>Or Debit / Credit Card</span>
                </div>
                <div id="card-name-field-container"></div>
                <div id="card-number-field-container"></div>
                <div class="card-fields">
                    <div id="card-expiry-field-container"></div>
                    <div id="card-cvv-field-container"></div>
                </div>
                <button id="card-field-submit-button" class="button wide upper green arrow" @click="handleCardSubmit" :disabled="processing">
                    <span>Pay with Card</span>
                </button>
                <div class="payment-logos">
                    <img src="/images/payment-logos.png" alt="Payment Logos">
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
    props: {
        kerbside: {
            type: Boolean,
            default: () => false,
            required: true,
        },
        kerbsideOption: {
            type: Boolean,
            default: () => false,
            required: true,
        },
        submittable: {
            type: Boolean,
            default: () => true,
            required: true,
        },
        agreeTerms: {
            type: Boolean,
            default: () => false,
            required: true,
        },
        total: {
            type: Number,
            default: () => 0,
            required: true,
        },
    },
    data() {
        return {
            cardField: null,
            processing: false,
            paymentsClient: null,
            allowedPaymentMethods: null,
            merchantInfo: null,
            baseRequest: {
                apiVersion: 2,
                apiVersionMinor: 0
            }
        }
    },
    mounted() {
        this.initialisePaypal();
        this.initialiseGooglePay();
    },
    methods: {
        ...mapActions(["addAlert"]),
        handleCardSubmit() {
            if (this.cardField) {
                this.processing = true;
                this.cardField.submit()
                    .catch((error) => {
                        this.processing = false;
                        if (error.message === 'INELIGIBLE_CARD_VENDOR') {
                            this.addAlert({
                                alertClass: 'alert-danger',
                                alertMessage: 'There was an error processing your card. Please try again.',
                                alertImportant: true,
                            });
                        }
                    });
            }
        },
        async checkOrderValidity() {
            try {
                const response = await axios.post('/ajax/checkOrderValidity');
                if (response.data.valid === false) {
                    alert(response.data.message);
                    window.location.href = '/basket';
                    return false;
                }
                return true;
            } catch (error) {
                window.location.href = '/basket';
                return false;
            }
        },
        async createOrder() {
            try {
                const response = await axios.get("/checkout/payment/paypal-get-order");
                return response.data.id;
            } catch (error) {
                this.addAlert({
                    alertClass: 'alert-danger',
                    alertMessage: 'Error creating PayPal order. Please try again.',
                    alertImportant: true,
                });
                throw error;
            }
        },
        async onApprove(data, actions) {
            try {

                const response = await axios.post('/checkout/payment/paypal-capture-order', { order_id: data.orderID }).catch(error => {
                    console.error('PayPal capture order error:', error);
                    this.processing = false;
                    window.location.href = "/basket";
                });

                const transaction = response.data?.purchase_units?.[0]?.payments?.captures?.[0] || response.data?.purchase_units?.[0]?.payments?.authorizations?.[0];
                const errorDetail = response.data?.details?.[0];

                if (errorDetail?.issue === "INSTRUMENT_DECLINED" && !data.card && actions) {
                    this.processing = false;
                    return actions.restart();
                }

                if (errorDetail || transaction?.status === "DECLINED") {
                    const errorMessage = transaction
                        ? `Transaction ${transaction.status}: ${transaction.id}`
                        : errorDetail
                            ? `${errorDetail.description} (${data.debug_id})`
                            : JSON.stringify(data);
                    this.addAlert({
                        alertClass: 'alert-danger',
                        alertMessage: `<strong>Error</strong> ${errorMessage}`,
                        alertImportant: true,
                    });
                    this.processing = false;
                }

                const postData = {
                    paypal_data: response.data,
                    opt_in: document.getElementById('opt_in')?.checked || false
                };

                try {
                    const response = await axios.post('/checkout/payment/paypal-confirmed', postData);
                    window.location.replace(response.data);
                } catch (error) {
                    console.error('PayPal confirmed error:', error);
                    this.processing = false;
                }

            } catch (error) {
                this.addAlert({
                    alertClass: 'alert-danger',
                    alertMessage: 'There was a problem with your payment. Please try again.',
                    alertImportant: true,
                });
                this.processing = false;
            }
        },
        async initialisePaypal() {
            const isValid = await this.checkOrderValidity();
            if (!isValid) return;

            paypal.Buttons({
                createOrder: () => this.createOrder(),
                onApprove: (data, actions) => this.onApprove(data, actions),
                style: {
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'rect',
                    label: 'paypal'
                },
                onError: (err) => {
                    console.error('PayPal error:', err);
                    window.location.href = "/basket";
                }
            }).render('#paypal-button-container');

            this.cardField = paypal.CardFields({
                createOrder: () => this.createOrder(),
                onApprove: (data, actions) => this.onApprove(data, actions),
                onError: (error) => {
                    this.processing = false;
                    let errorMessage = 'There was an error processing your card.';

                    try {
                        if (error.message) {
                            const jsonStart = error.message.indexOf('{');
                            if (jsonStart !== -1) {
                                const jsonString = error.message.slice(jsonStart);
                                const errorData = JSON.parse(jsonString);

                                if (errorData.details?.[0]?.description) {
                                    errorMessage = errorData.details[0].description;
                                }
                            }
                        }
                    } catch (e) {
                        this.processing = false;
                        console.error('Error parsing PayPal error:', e);
                    }

                    this.addAlert({
                        alertClass: 'alert-danger',
                        alertMessage: errorMessage,
                        alertImportant: true,
                    });
                }
            });

            if (this.cardField.isEligible()) {
                this.renderCardFields();
            } else {
                document.querySelector("#card-form").style.display = "none";
            }
        },
        renderCardFields() {
            this.cardField.NameField().render("#card-name-field-container");
            this.cardField.NumberField().render("#card-number-field-container");
            this.cardField.CVVField().render("#card-cvv-field-container");
            this.cardField.ExpiryField().render("#card-expiry-field-container");
        },

        async getGooglePayConfig() {
            if (!this.allowedPaymentMethods || !this.merchantInfo) {
                const googlePayConfig = await paypal.Googlepay().config();
                this.allowedPaymentMethods = googlePayConfig.allowedPaymentMethods;
                this.merchantInfo = googlePayConfig.merchantInfo;
            }
            return {
                allowedPaymentMethods: this.allowedPaymentMethods,
                merchantInfo: this.merchantInfo
            };
        },

        getGoogleIsReadyToPayRequest(allowedPaymentMethods) {
            return {
                ...this.baseRequest,
                allowedPaymentMethods
            };
        },

        async getGooglePaymentDataRequest() {
            const paymentDataRequest = { ...this.baseRequest };
            const { allowedPaymentMethods, merchantInfo } = await this.getGooglePayConfig();

            paymentDataRequest.allowedPaymentMethods = allowedPaymentMethods;
            paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();
            paymentDataRequest.merchantInfo = merchantInfo;
            paymentDataRequest.callbackIntents = ["PAYMENT_AUTHORIZATION"];

            return paymentDataRequest;
        },

        getGoogleTransactionInfo() {
            return {
                countryCode: 'GB',
                currencyCode: 'GBP',
                totalPriceStatus: 'FINAL',
                totalPrice: this.total.toString(),
                totalPriceLabel: 'Total'
            };
        },

        getGooglePaymentsClient() {
            if (!this.paymentsClient) {
                this.paymentsClient = new google.payments.api.PaymentsClient({
                    environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST',
                    paymentDataCallbacks: {
                        onPaymentAuthorized: this.onPaymentAuthorised
                    }
                });
            }
            return this.paymentsClient;
        },

        async onPaymentAuthorised(paymentData) {
            try {
                const response = await this.processGooglePayment(paymentData);
                return { transactionState: 'SUCCESS' };
            } catch (error) {
                console.error('Google Pay payment failed:', error);
                this.addAlert({
                    alertClass: 'alert-danger',
                    alertMessage: "There was a problem with your payment. Please try again.",
                    alertImportant: true,
                });
                return { transactionState: 'ERROR' };
            }
        },

        async processGooglePayment(paymentData) {
            try {
                const orderResponse = await this.createOrder();
                const { status } = await paypal.Googlepay().confirmOrder({
                    orderId: orderResponse,
                    paymentMethodData: paymentData.paymentMethodData
                });

                if (status === "APPROVED") {

                    const captureResponse = await axios.post('/checkout/payment/paypal-capture-order', { order_id: orderResponse }).catch(error => {
                        console.error('PayPal capture order error:', error);
                    });

                    const postData = {
                        paypal_data: captureResponse.data,
                        opt_in: document.getElementById('opt_in')?.checked || false
                    };

                    const response = await axios.post('/checkout/payment/paypal-confirmed', postData);
                    window.location.replace(response.data);
                    return true;
                }
                throw new Error('Payment not approved');
            } catch (error) {
                console.error('Process payment error:', error);
                this.processing = false;
                throw error;
            }
        },

        async initialiseGooglePay() {
            try {

                if (typeof google === 'undefined' || !google.payments) {
                return;
            }
                const paymentsClient = this.getGooglePaymentsClient();
                const { allowedPaymentMethods } = await this.getGooglePayConfig();

                const response = await paymentsClient.isReadyToPay(
                    this.getGoogleIsReadyToPayRequest(allowedPaymentMethods)
                );

                if (response.result) {
                    const button = paymentsClient.createButton({
                        onClick: async () => {
                            const paymentDataRequest = await this.getGooglePaymentDataRequest();
                            paymentsClient.loadPaymentData(paymentDataRequest);
                        }
                    });
                    document.getElementById('google-pay-container').appendChild(button);
                }
            } catch (error) {
                console.error('Google Pay initialization error:', error);
            }
        },
    },
};

</script>
